// Sugalarm — Cycle 76: Settings → Reminders pane.
//
// A scrollable list of reminder cards (message + time/repeat summary +
// optional member chip + enabled toggle + Test/Edit/Delete) and an
// inline Add/Edit editor (message, time, weekly day-picker or one-off
// date, optional member, timezone prefilled from the browser). The
// kiosk OnScreenKeyboard auto-summons on the message input focus (it
// listens on document-level focus — no wiring here). Fits 1024×600:
// the pane scrolls; the editor is an inline card, not a modal.

import { useEffect, useState } from "react";

import { ConfettiCelebration } from "../../components/ui/ConfettiCelebration";
import * as membersApi from "../../api/members";
import * as remindersApi from "../../api/reminders";
import type {
  Member,
  Reminder,
  ReminderCreateIn,
  ReminderUpdateIn,
  WeekdayCode,
} from "../../lib/types";
import { SubsectionCard } from "./SubsectionCard";
import { TimePicker } from "../../components/ui/TimePicker";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { useConfirm } from "../../lib/confirmContext";
import styles from "./RemindersSection.module.css";

const DAYS: { code: WeekdayCode; short: string }[] = [
  { code: "mon", short: "Mon" },
  { code: "tue", short: "Tue" },
  { code: "wed", short: "Wed" },
  { code: "thu", short: "Thu" },
  { code: "fri", short: "Fri" },
  { code: "sat", short: "Sat" },
  { code: "sun", short: "Sun" },
];

const ALL_DAYS: WeekdayCode[] = DAYS.map((d) => d.code);

const BellIcon = (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M4.5 13h9l-1.2-1.6V8a3.3 3.3 0 1 0-6.6 0v3.4L4.5 13Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M7.5 14.5a1.5 1.5 0 0 0 3 0"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 18 18",
  fill: "none" as const,
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

// Test → paper-plane (send/fire now)
const TestIcon = (
  <svg {...svgProps} aria-hidden>
    <path d="M15.5 3 L2.5 8 L7.5 9.6 L9.4 14.5 Z" />
    <path d="M7.5 9.6 L15.5 3" />
  </svg>
);
// Edit → ✏️ emoji (cycle 218). Decorative glyph only — the button's
// `title` + `aria-label` carry the real accessible name "Edit". The
// emoji is fixed-color and bypasses the color tokens by design; the
// button shell (fill/border/hover) stays token-driven.
const EditIcon = (
  <span className={styles.emojiGlyph} aria-hidden>
    ✏️
  </span>
);
// Delete → 🗑️ emoji (cycle 218). Decorative; "Delete" stays the
// accessible name. The button keeps its destructive red hover on
// fill/border — only the glyph became the emoji.
const DeleteIcon = (
  <span className={styles.emojiGlyph} aria-hidden>
    🗑️
  </span>
);
// Sent confirmation → check
const CheckIcon = (
  <svg {...svgProps} aria-hidden>
    <path d="M4 9.5 L7.5 13 L14 5" />
  </svg>
);

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

function fmtClock(hhmm: string, h24: boolean): string {
  const [hs, ms] = hhmm.split(":");
  const h = Number(hs);
  const m = ms ?? "00";
  if (h24) return `${String(h).padStart(2, "0")}:${m}`;
  const ampm = h < 12 ? "AM" : "PM";
  let hr = h % 12;
  if (hr === 0) hr = 12;
  return `${hr}:${m} ${ampm}`;
}

function fmtOnceDate(iso: string): string {
  // iso = "YYYY-MM-DD" — construct a local Date to avoid UTC drift.
  const [y, mo, d] = iso.split("-").map(Number);
  if (!y || !mo || !d) return iso;
  return new Date(y, mo - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function repeatSummary(r: Reminder, h24: boolean): string {
  const clock = fmtClock(r.time_of_day, h24);
  if (r.one_off_date) {
    return `Once · ${fmtOnceDate(r.one_off_date)} · ${clock}`;
  }
  if (r.weekdays.length === 7) return `Every day · ${clock}`;
  if (r.weekdays.length === 0) return clock;
  const labels = DAYS.filter((d) => r.weekdays.includes(d.code)).map(
    (d) => d.short,
  );
  return `${labels.join(", ")} · ${clock}`;
}

type RepeatMode = "daily" | "weekly" | "once";

interface DraftState {
  id: string | null; // null = creating
  message: string;
  time: string; // "HH:MM"
  mode: RepeatMode;
  weekdays: Set<WeekdayCode>;
  onceDate: string; // "YYYY-MM-DD"
  memberId: string; // "" = none
  timezone: string;
}

function emptyDraft(): DraftState {
  return {
    id: null,
    message: "",
    time: "10:00",
    mode: "daily",
    weekdays: new Set<WeekdayCode>(ALL_DAYS),
    onceDate: "",
    memberId: "",
    timezone: browserTimezone(),
  };
}

function draftFromReminder(r: Reminder): DraftState {
  const mode: RepeatMode = r.one_off_date
    ? "once"
    : r.weekdays.length === 7
      ? "daily"
      : "weekly";
  return {
    id: r.id,
    message: r.message,
    time: r.time_of_day,
    mode,
    weekdays: new Set(r.weekdays.length ? r.weekdays : ALL_DAYS),
    onceDate: r.one_off_date ?? "",
    memberId: r.member_id ?? "",
    timezone: r.timezone,
  };
}

export function RemindersSection({
  h24,
  variant,
}: {
  h24: boolean;
  variant?: FrameVariant;
}) {
  const { confirm } = useConfirm();
  const [reminders, setReminders] = useState<Reminder[] | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [saving, setSaving] = useState(false);
  const [testedId, setTestedId] = useState<string | null>(null);
  const [celebrate, setCelebrate] = useState(false);

  async function load() {
    const r = await remindersApi.list();
    if (r.ok) setReminders(r.data);
    else setError(r.error.message);
  }

  useEffect(() => {
    void load();
    void (async () => {
      const r = await membersApi.listMine();
      if (r.ok) setMembers(r.data.members.filter((m) => m.archived_at == null));
    })();
  }, []);

  // Cycle 217: cross-device reminder sync. App.tsx re-broadcasts the
  // `reminders_changed` WS frame (a create/edit/delete on any of this
  // owner's devices) as this window event; reload so the change shows
  // instantly without navigating away and back. `load` only touches the
  // stable api + state setters, so the first-render closure stays valid.
  useEffect(() => {
    const onChanged = () => {
      void load();
    };
    window.addEventListener("sugalarm:reminders-changed", onChanged);
    return () =>
      window.removeEventListener("sugalarm:reminders-changed", onChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onToggle(r: Reminder) {
    const res = await remindersApi.update(r.id, { enabled: !r.enabled });
    if (res.ok) {
      setReminders((prev) =>
        (prev ?? []).map((x) => (x.id === r.id ? res.data : x)),
      );
    } else {
      setError(res.error.message);
    }
  }

  async function onDelete(r: Reminder) {
    if (!(await confirm({ message: `Delete reminder “${r.message}”?`, destructive: true }))) return;
    const res = await remindersApi.remove(r.id);
    if (res.ok) {
      setReminders((prev) => (prev ?? []).filter((x) => x.id !== r.id));
    } else {
      setError(res.error.message);
    }
  }

  async function onTest(r: Reminder) {
    const res = await remindersApi.test(r.id);
    if (res.ok) {
      setTestedId(r.id);
      window.setTimeout(
        () => setTestedId((cur) => (cur === r.id ? null : cur)),
        2000,
      );
    } else {
      setError(res.error.message);
    }
  }

  function toggleDay(code: WeekdayCode) {
    setDraft((d) => {
      if (!d) return d;
      const next = new Set(d.weekdays);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return { ...d, weekdays: next };
    });
  }

  async function onSave() {
    if (!draft) return;
    setError(null);
    const message = draft.message.trim();
    if (!message) {
      setError("Enter a reminder message.");
      return;
    }
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(draft.time)) {
      setError("Pick a valid time.");
      return;
    }
    if (!draft.timezone.trim()) {
      setError("Timezone is required.");
      return;
    }
    if (draft.mode === "weekly" && draft.weekdays.size === 0) {
      setError("Pick at least one weekday (or choose Daily / Once).");
      return;
    }
    if (draft.mode === "once" && !draft.onceDate) {
      setError("Pick a date for the one-off reminder.");
      return;
    }

    const weekdays: WeekdayCode[] | undefined =
      draft.mode === "daily"
        ? ALL_DAYS
        : draft.mode === "weekly"
          ? DAYS.filter((d) => draft.weekdays.has(d.code)).map((d) => d.code)
          : undefined;

    const wasCreate = draft.id == null;
    setSaving(true);
    let res;
    if (wasCreate) {
      const body: ReminderCreateIn = {
        message,
        time_of_day: draft.time,
        timezone: draft.timezone.trim(),
        member_id: draft.memberId || null,
      };
      if (draft.mode === "once") body.one_off_date = draft.onceDate;
      else body.weekdays = weekdays;
      res = await remindersApi.create(body);
    } else {
      const body: ReminderUpdateIn = {
        message,
        time_of_day: draft.time,
        timezone: draft.timezone.trim(),
        member_id: draft.memberId || null,
      };
      if (draft.mode === "once") body.one_off_date = draft.onceDate;
      else body.weekdays = weekdays;
      res = await remindersApi.update(draft.id!, body);
    }
    setSaving(false);
    if (!res.ok) {
      setError(res.error.message);
      return;
    }
    setDraft(null);
    await load();
    if (wasCreate) setCelebrate(true);
  }

  const editing = draft != null;

  return (
    <div className={styles.stack}>
      <SubsectionCard
        title="Reminders"
        subtitle="Recurring or one-off nudges — push, on-screen, and spoken on the kiosk."
        icon={BellIcon}
        tone="accent"
        headerRight={
          !editing ? (
            <button
              type="button"
              className={styles.addBtn}
              onClick={() => setDraft(emptyDraft())}
            >
              + Add
            </button>
          ) : undefined
        }
      >
        {error ? <div className={styles.error}>{error}</div> : null}

        {editing ? (
          <div className={styles.editor}>
            <div className={styles.field}>
              <label className={styles.label}>Message</label>
              <input
                type="text"
                className={styles.input}
                value={draft!.message}
                maxLength={500}
                placeholder="e.g. Give evening insulin"
                onChange={(e) =>
                  setDraft((d) => (d ? { ...d, message: e.target.value } : d))
                }
              />
            </div>

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.label}>Time</label>
                {/* Cycle 243.5: the kiosk uses a custom branded picker — the
                    native <input type="time"> clock dialog clips its OK
                    button off the short kiosk landscape viewport. Web/mobile
                    keep the native picker (works fine there). */}
                {variant === "kiosk" ? (
                  <TimePicker
                    value={draft!.time}
                    h24={h24}
                    onChange={(t) =>
                      setDraft((d) => (d ? { ...d, time: t } : d))
                    }
                  />
                ) : (
                  <input
                    type="time"
                    className={styles.input}
                    value={draft!.time}
                    onChange={(e) =>
                      setDraft((d) => (d ? { ...d, time: e.target.value } : d))
                    }
                  />
                )}
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Repeat</label>
                <span className={styles.seg}>
                  {(["daily", "weekly", "once"] as RepeatMode[]).map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`${styles.segItem} ${
                        draft!.mode === m ? styles.segItemActive : ""
                      }`}
                      onClick={() =>
                        setDraft((d) => (d ? { ...d, mode: m } : d))
                      }
                    >
                      {m === "daily" ? "Daily" : m === "weekly" ? "Weekly" : "Once"}
                    </button>
                  ))}
                </span>
              </div>
            </div>

            {draft!.mode === "daily" ? (
              <div className={styles.dailyHint}>Fires every day at this time.</div>
            ) : draft!.mode === "weekly" ? (
              <div className={styles.field}>
                <label className={styles.label}>Days</label>
                <div className={styles.dayRow}>
                  {DAYS.map((d) => (
                    <button
                      key={d.code}
                      type="button"
                      className={`${styles.dayChip} ${
                        draft!.weekdays.has(d.code) ? styles.dayChipOn : ""
                      }`}
                      onClick={() => toggleDay(d.code)}
                    >
                      {d.short}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className={styles.field}>
                <label className={styles.label}>Date</label>
                <input
                  type="date"
                  className={styles.input}
                  value={draft!.onceDate}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, onceDate: e.target.value } : d,
                    )
                  }
                />
              </div>
            )}

            <div className={styles.fieldRow}>
              <div className={styles.field}>
                <label className={styles.label}>About (optional)</label>
                <select
                  className={styles.input}
                  value={draft!.memberId}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, memberId: e.target.value } : d,
                    )
                  }
                >
                  <option value="">No one</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.display_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Timezone</label>
                <input
                  type="text"
                  className={styles.input}
                  value={draft!.timezone}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, timezone: e.target.value } : d,
                    )
                  }
                />
              </div>
            </div>

            <div className={styles.editorActions}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => {
                  setDraft(null);
                  setError(null);
                }}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => void onSave()}
                disabled={saving}
              >
                {saving ? "Saving…" : draft!.id == null ? "Add" : "Save"}
              </button>
            </div>
          </div>
        ) : reminders == null ? (
          <div className={styles.muted}>Loading…</div>
        ) : reminders.length === 0 ? (
          <div className={styles.empty}>
            No reminders yet. Tap “+ Add” to create one.
          </div>
        ) : (
          <div className={styles.list}>
            {reminders.map((r) => (
              <div
                key={r.id}
                className={`${styles.row} ${r.enabled ? "" : styles.rowOff}`}
              >
                <div className={styles.rowMain}>
                  <div className={styles.rowMsg}>{r.message}</div>
                  <div className={styles.rowMeta}>
                    {repeatSummary(r, h24)}
                    {r.member_name ? (
                      <span className={styles.memberChip}>
                        {r.member_name}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className={styles.rowActions}>
                  {testedId === r.id ? (
                    <span className={styles.iconOk} title="Sent" aria-label="Sent">
                      {CheckIcon}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => void onTest(r)}
                      title="Test now"
                      aria-label="Test reminder now"
                    >
                      {TestIcon}
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={() => setDraft(draftFromReminder(r))}
                    title="Edit"
                    aria-label="Edit reminder"
                  >
                    {EditIcon}
                  </button>
                  <button
                    type="button"
                    className={styles.iconBtnDanger}
                    onClick={() => void onDelete(r)}
                    title="Delete"
                    aria-label="Delete reminder"
                  >
                    {DeleteIcon}
                  </button>
                  <button
                    type="button"
                    aria-pressed={r.enabled}
                    className={`${styles.toggle} ${
                      r.enabled ? styles.toggleOn : ""
                    }`}
                    onClick={() => void onToggle(r)}
                    aria-label={r.enabled ? "Disable reminder" : "Enable reminder"}
                  >
                    <span className={styles.toggleKnob} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SubsectionCard>

      <ConfettiCelebration
        open={celebrate}
        title="Reminder set!"
        subtitle="You'll get a push, an on-screen banner, and a spoken cue on the kiosk when it's time."
        onClose={() => setCelebrate(false)}
      />
    </div>
  );
}
